import React, { useContext, useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../components/Dropdown';
import { Tabs, TabsList, TabsTrigger } from '../../components/Tabs';
import { Badge } from '../../components/Badge';
import { Link } from '../../components/Link';
import { Button } from '../../components/Button';
import { Heading } from '../../components/Heading';
import { Divider } from '../../components/Divider';
import { UserService } from '../../services/userService';
import { Avatar } from '../../components/Avatar';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import useUserService from '../../hooks/useUserService';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../components/Dialog';
import { UserContext } from '../../context/userContext';
import { useRoles } from '../../hooks/useRoles';
import { User } from '../../models/User';
import { Card } from '../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu } from '../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../components/FiltersDialog';
import { DateRangePicker } from '../../components/DateRangePicker';
import { Select } from '../../components/Select';
import { Text } from '../../components/Text';
import { useWarehouses } from '../../hooks/useWarehouse';

const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const UsersPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { loading, error, totalUsers, users, deleteUser } = useUserService();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [usersPerPage] = useState<number>(10);
    const [selectedRole, setSelectedRole] = useState<string | null>('All'); // Default to 'All'
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const navigate = useNavigate();
    const { roles } = useRoles();
    const { warehouses } = useWarehouses();
    const [sortOrder, setSortOrder] = useState('az');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');

    useEffect(() => {
        document.title = "Users";
    }, []);

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(users) ? users : [])
                .map((u) => new Date(u.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    // Filter users based on the search query and selected role, excluding current user
    const filteredUsers = Array.isArray(users)
        ? users.filter((user) => {
            const matchesSearch = user.fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesRole = selectedRole === 'All' || user.rolename === selectedRole;
            const isNotCurrentUser = user.id !== currentUser!.id;
            const matchesStartDate = !filterStartDate || new Date(user.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(user.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(user.created_time).getFullYear().toString() === filterYear;
            const matchesWarehouse = !filterWarehouseId || user.warehouse_id === filterWarehouseId;
            return matchesSearch && matchesRole && isNotCurrentUser && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
        }) : [];

    // Pagination logic
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const sortedUsers = [...filteredUsers].sort((a, b) => sortOrder === 'az' ? a.fullname.localeCompare(b.fullname) : b.fullname.localeCompare(a.fullname));
    const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    // Open the delete dialog with the selected user's ID
    const handleOpenDeleteDialog = (userId: string) => {
        setSelectedUserId(userId);
        setIsOpen(true);
    };

    // Handle delete user
    const handleDelete = async () => {
        if (!selectedUserId) return;
        setIsDeleting(true);
        try {
            await deleteUser(selectedUserId);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete user:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>User Account</Heading>
                </div>
                <Button onClick={() => navigate('/gp/users/add')}><PlusIcon />Create new user</Button>
            </div>

            <Card className="mt-6 p-5">
                <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search user…" />}>
                    <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                    <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                </ListToolbarRow>
                <FilterPills pills={filterPills} onClearAll={resetFilters} />
                {/* Tab navigation */}
                <div className="sm:hidden">
                    <label htmlFor="tabs" className="sr-only">Select a tab</label>
                    <select
                        id="tabs"
                        name="tabs"
                        value={selectedRole!}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="block w-full rounded-full bg-secondary py-2 pl-4 pr-10 text-base focus:outline-none focus:ring-2 focus:ring-ring sm:text-sm"
                    >
                        <option value="All">All</option>
                        {roles.map((role) => (
                            <option key={role.rolename} value={role.rolename}>
                                {role.rolename}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="hidden sm:block">
                    <Tabs value={selectedRole!} onValueChange={setSelectedRole}>
                        <TabsList>
                            <TabsTrigger value="All">All</TabsTrigger>
                            {roles.map((role) => (
                                <TabsTrigger key={role.rolename} value={role.rolename}>
                                    {role.rolename}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                <ul className="mt-2 divide-y divide-border/0">
                    {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                    {error && <p className="py-16 text-center text-red-700">{error}</p>}
                    {!loading && !error && currentUsers.length === 0 && <p className="py-16 text-center">No user found.</p>}
                    {!loading && !error && currentUsers.map((user) => (
                        <li key={user.id} className="rounded-xl hover:bg-secondary">
                            <div className="flex items-center justify-between px-2">
                                <div className="flex gap-6 py-5">
                                    <Avatar square initials={user.fullname[0]} className="bg-brand-teal/10 text-brand-teal size-12" />
                                    <div className="">
                                        <div className="text-base/6 font-semibold">
                                            <Link href={`/gp/users/view/${user.id}`}>{user.fullname}</Link>
                                        </div>
                                        <div className="text-xs/6 text-muted-foreground">
                                            {user.email ? user.email : 'Undefined email'}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <Badge className="max-sm:hidden" color='lime'>
                                        {user.rolename.toUpperCase()}
                                    </Badge>
                                    <Dropdown>
                                        <DropdownButton plain aria-label="More options">
                                            <EllipsisVerticalIcon />
                                        </DropdownButton>
                                        <DropdownMenu anchor="bottom end">
                                            <DropdownItem onClick={() => navigate(`/gp/users/view/${user.id}`)}>View</DropdownItem>
                                            <DropdownItem onClick={() => navigate(`/gp/users/edit/${user.id}`)}>Edit</DropdownItem>
                                            <DropdownItem onClick={() => handleOpenDeleteDialog(user.id!.toString())}>Delete</DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    totalResult={totalUsers}
                    dataName='Users'
                />
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete User</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this user? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UsersPage;
